import { useCustomerCompanyList } from "@boundless/bl-api-client/customer/services"
import { Building2 } from "lucide-react"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { companyListQueryOptions, isCurrentUserOwner } from "@/lib/companies"

interface Props {
  companyUuid: string
  onSelect: (companyUuid: string) => void
}

/**
 * Scopes a Users page to one of the account's companies without navigating
 * away. Renders nothing when there is nothing to pick between.
 */
export function CompanyFilter({ companyUuid, onSelect }: Props) {
  // Same cache key `CompanyWrapper` populates, so this reads the list the
  // dashboard already has rather than issuing a second request. The shared
  // options are mandatory, not a convenience - see `companyListQueryOptions`.
  const { data: companies } = useCustomerCompanyList(
    {},
    companyListQueryOptions(),
  )

  // Companies the account only collaborates on would bounce the user straight
  // back out of the page, so they are not offered.
  const owned = companies?.filter(isCurrentUserOwner) ?? []

  if (owned.length < 2) {
    return null
  }

  // Copy before sorting: `companies` is TanStack Query's cached array.
  const sorted = [...owned].sort((a, b) =>
    a.legalName.localeCompare(b.legalName),
  )

  // Width is capped rather than `sm:w-72`: the table below is wider than a
  // phone viewport, so a full-width trigger stretches off-screen with it.
  return (
    <div className="w-full max-w-72 space-y-2">
      <Label className="text-base font-medium">Select company</Label>
      <Select value={companyUuid} onValueChange={onSelect}>
        <SelectTrigger className="h-9 w-full lg:!h-9">
          <Building2 className="text-muted-foreground size-4 shrink-0" />
          <SelectValue placeholder="Select company" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {sorted.map((company) => (
              <SelectItem key={company.uuid} value={company.uuid}>
                {company.legalName}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}
